import { Fragment, useLayoutEffect, useRef, useState, type MouseEvent, type PointerEvent, type ReactNode, type Ref } from 'react';
import type { Drawing, Pin } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { fileUrl as defaultFileUrl } from '../file-url';
import { clientToUV, drawingRect, type DrawingRect, type Point, type Size } from '../stage/geometry';
import { PinMarker, type LabelDensity, type PinDotVariant } from './pins';

export interface DrawingViewProps {
  drawing: Drawing | null;
  pins: readonly Pin[];
  /** label density of the folder (`labelDensity`) */
  density?: LabelDensity;
  /** dot look: `viewer` (590:16559, 23 px halo + 9 px dot) or `editor` (429:17364, 14 px dot + ring) */
  variant?: PinDotVariant;
  /** render hidden pins dimmed (editor) instead of skipping them */
  showHidden?: boolean;
  selectedPinId?: string | null;
  /** extra zoom on top of fit × `drawing.scale` — editor navigation, never persisted */
  zoom?: number;
  /** pan in CSS px (editor navigation) */
  pan?: Point;
  fileUrl?: (fileId: string) => string;
  onPinClick?: (pin: Pin, e: MouseEvent) => void;
  onPinPointerDown?: (pin: Pin, e: PointerEvent) => void;
  /** accessible name of an interactive pin that has no title or rows yet */
  pinFallbackLabel?: string;
  /** pointer down on the canvas outside pins; `uv` is null outside the artwork (placement mode ignores it) */
  onCanvasPointerDown?: (uv: { u: number; v: number } | null, e: PointerEvent<HTMLDivElement>) => void;
  /** wrap a pin (editor drag handles, guides) */
  renderPin?: (pin: Pin, node: ReactNode) => ReactNode;
  /** called when the drawing box moves/resizes (CSS px inside the viewport) */
  onLayout?: (rect: DrawingRect, viewport: Size) => void;
  /** ref to the element whose box is exactly the image — use with `clientToUV()` */
  boxRef?: Ref<HTMLDivElement>;
  /** overlay inside the image box (positions in % of the image) */
  children?: ReactNode;
  /** shown when there is no drawing */
  emptyState?: ReactNode;
  className?: string;
  'aria-label'?: string;
}

/**
 * Technical drawing with live sensor pins (02-constructor §6.5–6.6, §8.4).
 *
 * The image is contain-fitted into the viewport × `drawing.scale` (× `zoom`), and the pins live in the same box at
 * `left: u·100 %, top: v·100 %` — so a pin stays on the same feature of the artwork at any size, zoom or panel
 * layout (no `%` of a padded container — the legacy drift). Dots and label cards are UI-sized (design px, scale
 * with the page, not with the drawing zoom); labels are placed by `label.side / distance / offset`.
 */
export function DrawingView({
  drawing,
  pins,
  density = 'comfortable',
  variant = 'viewer',
  showHidden = false,
  selectedPinId = null,
  zoom = 1,
  pan,
  fileUrl = defaultFileUrl,
  onPinClick,
  onPinPointerDown,
  pinFallbackLabel,
  onCanvasPointerDown,
  renderPin,
  onLayout,
  boxRef,
  children,
  emptyState,
  className,
  ...rest
}: DrawingViewProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<Size | null>(null);

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setViewport((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rect =
    drawing && viewport && viewport.w > 0 && viewport.h > 0
      ? drawingRect(viewport, { w: drawing.naturalWidth, h: drawing.naturalHeight }, drawing.scale * zoom, pan)
      : null;

  const onLayoutRef = useRef(onLayout);
  useLayoutEffect(() => {
    onLayoutRef.current = onLayout;
  });
  const rx = rect?.x;
  const ry = rect?.y;
  const rw = rect?.w;
  const rh = rect?.h;
  useLayoutEffect(() => {
    if (rx === undefined || ry === undefined || rw === undefined || rh === undefined || !viewport || !drawing) return;
    onLayoutRef.current?.({ x: rx, y: ry, w: rw, h: rh, k: rw / drawing.naturalWidth }, viewport);
  }, [rx, ry, rw, rh, viewport, drawing]);

  const handleCanvasDown = onCanvasPointerDown
    ? (e: PointerEvent<HTMLDivElement>) => {
        if ((e.target as Element).closest('[data-pin-id]')) return;
        const box = viewportRef.current?.querySelector('[data-drawing-box]');
        const uv = box ? clientToUV(e, box) : null;
        onCanvasPointerDown(uv && uv.inside ? { u: uv.u, v: uv.v } : null, e);
      }
    : undefined;

  return (
    <div ref={viewportRef} className={cn('relative size-full overflow-hidden', className)} onPointerDown={handleCanvasDown} {...rest}>
      {!drawing
        ? emptyState
        : rect && (
            <div
              ref={boxRef}
              data-drawing-box=""
              className="absolute"
              style={{ left: `${rect.x}px`, top: `${rect.y}px`, width: `${rect.w}px`, height: `${rect.h}px` }}
            >
              <img
                src={fileUrl(drawing.file.fileId)}
                alt=""
                draggable={false}
                decoding="async"
                className="pointer-events-none absolute inset-0 size-full max-w-none select-none"
                style={{ opacity: drawing.opacity }}
              />
              {children}
              {pins.map((pin) => {
                if (pin.hidden && !showHidden) return null;
                const node = (
                  <PinMarker
                    pin={pin}
                    density={density}
                    variant={variant}
                    selected={pin.id === selectedPinId}
                    dimmed={pin.hidden}
                    onClick={onPinClick}
                    onPointerDown={onPinPointerDown}
                    fallbackLabel={pinFallbackLabel}
                  />
                );
                return <Fragment key={pin.id}>{renderPin ? renderPin(pin, node) : node}</Fragment>;
              })}
            </div>
          )}
    </div>
  );
}
